// Active-project session: which project is currently open in this browser
// session. Backed by `sessionStorage` so each tab can have its own active
// project, with localStorage events broadcast so other tabs/components
// react to changes without polling.
//
// `LEGACY_SESSION_ID_KEY` is read for back-compat with older client versions
// that wrote `sessionid` alongside `projectid`. We never write to it from
// new code; `App.jsx` does a one-shot cleanup on startup.

const ACTIVE_PROJECT_ID_KEY = "projectid";
const ACTIVE_PROJECT_NAME_KEY = "projectname";
const LEGACY_SESSION_ID_KEY = "sessionid";

// Phase I3: localStorage keys that hold state scoped to the currently
// active project. Clearing them on switch / clear prevents the stale
// flash-of-wrong-state when flipping projects, and keeps a shared device
// from exposing one user's project data to the next.
const PROJECT_SCOPED_STORAGE_KEYS = [
  "router_data",
  "router_final",
  "switch_data",
  "switch_final",
  "vlan_data",
  "hasConnectors"
];

export function clearProjectCaches({ projectId } = {}) {
  for (const key of PROJECT_SCOPED_STORAGE_KEYS) {
    try {
      localStorage.removeItem(key);
    } catch {
      // ignore storage errors
    }
  }
  // Also drop the per-project has-connectors flag for the outgoing project;
  // keep other projects' cached flags so the sidebar can pre-render.
  if (projectId) {
    try {
      localStorage.removeItem(`hasConnectors_${projectId}`);
    } catch {
      // ignore
    }
  }
}

export function getActiveProjectId() {
  return (
    sessionStorage.getItem(ACTIVE_PROJECT_ID_KEY) ||
    sessionStorage.getItem(LEGACY_SESSION_ID_KEY) ||
    ""
  );
}

export function getActiveProjectName() {
  return sessionStorage.getItem(ACTIVE_PROJECT_NAME_KEY) || "";
}

/**
 * Set or clear the active project. Pass `null` (or anything without a
 * project id) to clear. Always emits a `storage` event so listeners that
 * mirror the active project (sidebar, header, etc.) re-read.
 */
export function setActiveProject(project) {
  const projectId = project?.$id || project?.projectId || "";
  const projectName = project?.name || "";
  const previousProjectId = getActiveProjectId();

  if (!projectId) {
    sessionStorage.removeItem(ACTIVE_PROJECT_ID_KEY);
    sessionStorage.removeItem(ACTIVE_PROJECT_NAME_KEY);
    sessionStorage.removeItem(LEGACY_SESSION_ID_KEY);
    // Phase I3: wipe project-scoped caches so the editors don't show the
    // previous project's data until the next syncdown completes.
    clearProjectCaches({ projectId: previousProjectId });
    window.dispatchEvent(new Event("storage"));
    return;
  }

  // Phase I3: when switching projects (not setting for the first time),
  // flush caches scoped to the outgoing project. Skip the flush when we're
  // re-selecting the same project — that's just a re-hydrate, not a switch.
  if (previousProjectId && previousProjectId !== projectId) {
    clearProjectCaches({ projectId: previousProjectId });
  }

  sessionStorage.setItem(ACTIVE_PROJECT_ID_KEY, projectId);
  sessionStorage.setItem(ACTIVE_PROJECT_NAME_KEY, projectName);
  // Drop any leftover legacy key from older client versions.
  sessionStorage.removeItem(LEGACY_SESSION_ID_KEY);

  // Mirror the cached "this project has connectors" flag so the sidebar
  // can render the gated entries without a network round-trip.
  const cachedHas = localStorage.getItem(`hasConnectors_${projectId}`) === "1";
  localStorage.setItem("hasConnectors", cachedHas ? "1" : "0");
  window.dispatchEvent(
    new CustomEvent("hasConnectorsChanged", { detail: cachedHas })
  );

  window.dispatchEvent(new Event("storage"));
}

export function clearActiveProject() {
  setActiveProject(null);
}
